import axios from 'axios'
import * as constants from '@/utils/constants/constants.js'
import alert_mixin from '@/utils/mixins/alert_mixin';

export default {
  name: 'AssetRegistry',
  mixins: [alert_mixin],
  props: {
    applicationId: {
      type: Number,
      required: true
    },
    entityProfileId: {
      type: Number,
      required: true
    },
    projectId: {
      type: Number,
      required: true
    },
    parentEntityTypeId: {
      type: Number,
      default: null
    }
  },

  data :() => ({
      headers: constants.get_headers(),
      assets: [],
      validation_error: '',
      is_loading: false,
      is_saving: false,
      assign_sub_entity_options: [], // All entity types
      assign_sub_entity_type_id: '',
      assign_form_list: [],
      assign_selected_form_id: '',
      assign_entity_name: ''
  }),
    
  computed: {
    total_assets() {
      return this.assets ? this.assets.length : 0
    },

    active_assets() {
      return this.assets ? this.assets.filter(asset => this.is_asset_active(asset)).length : 0
    },

    portfolio_value() {
      return 0
    },

    formatted_portfolio_value() {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR'
      }).format(this.portfolio_value)
    },

    is_valid_form() {
      return this.assign_entity_name.trim() && 
             this.assign_sub_entity_type_id && 
             this.assign_selected_form_id
    },

    // Filter entity types to show only those with "asset" or "assets" in their name
    filtered_asset_options() {
      if (!this.assign_sub_entity_options || this.assign_sub_entity_options.length === 0) {
        return []
      }
      
      return this.assign_sub_entity_options.filter(option => {
        if (!option.name) return false
        
        const name = option.name.toLowerCase()
        // Check for "asset" or "assets" in the name
        return name.includes('asset') || name.includes('assets')
      })
    }
  },

  mounted() {
    this.load_assets()
  },

  methods: {
    open_add_asset_modal() {
      this.prepare_assign_modal()
        .catch(err => {
          this.show_error('Failed to prepare asset modal: ' + err.message)
        })
    },

    get_asset_status(asset) {
      if (asset.is_active !== undefined) {
        return asset.is_active ? 'Active' : 'Inactive'
      }
      if (asset.status) {
        return asset.status.status || asset.status
      }
      return 'Unknown'
    },

    get_status_class(asset) {
      const status = this.get_asset_status(asset).toLowerCase()
      if (status === 'active') {
        return 'bg-success bg-opacity-10 text-white'
      } else if (status === 'inactive') {
        return 'bg-danger bg-opacity-10 text-white'
      }
      return 'bg-secondary bg-opacity-10 text-white'
    },

    is_asset_active(asset) {
      const status = this.get_asset_status(asset).toLowerCase()
      return status === 'active'
    },

    load_assets() {
      this.is_loading = true
      
      axios.get(constants.LIST_SUB_ENTITIES_BY_PARENT_APPLICATION, {
        headers: this.headers,
        params: {
          parent_application_id: this.applicationId,
          entity_profile_id: this.entityProfileId
        }
      })
      .then(response => {
        let data = response.data
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data)
          } catch (parseError) {
            this.show_error('Error getting response:', parseError)
            data = {}
          }
        }

        if (data?.status === 'success') {
          this.assets = Array.isArray(data.data) ? data.data : (data.data?.children || [])
        } else {
          this.assets = []
          if (data?.message) {
            this.show_error(data.message)
          }
        }
      })
      .catch(() => {
        this.assets = []
      })
      .finally(() => {
        this.is_loading = false
      })
    },

    prepare_assign_modal() {
      this.reset_modal()
      this.is_loading = true
      this.validation_error = null

      const parentEntityTypeId = this.parentEntityTypeId || 
                                 this.$route?.query?.entity_type_id || 
                                 sessionStorage.getItem('entity_type_id')

      return this.get_assigned_children(parentEntityTypeId)
        .then(assigned_rows => {
          // Store all entity types
          this.assign_sub_entity_options = (assigned_rows || []).map(r => ({
            id: Number(r.child_entity_type_id),
            name: r.child_entity_type_name,
          }))

          // Show warning if no asset types found
          if (this.filtered_asset_options.length === 0) {
            if (this.assign_sub_entity_options.length > 0) {
              this.validation_error = "No entity types with 'Asset' in their name were found. Please create an entity type containing 'Asset' in its name."
            } else {
              this.validation_error = "No entity types available for this entity."
            }
          }
        })
        .catch(error => {
          this.show_error('Failed to load entity types: ' + error.message)
        })
        .finally(() => {
          this.is_loading = false
        })
    },

    get_assigned_children(parent_entity_type_id) {
      if (!parent_entity_type_id) return Promise.resolve([])

      return axios.get(
        constants.LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
        {
          headers: this.headers,
          params: { parent_entity_type_id },
        }
      )
      .then(res => {
        let data = res.data
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data)
          } catch (parseError) {
            this.show_error('Error getting assigned children:', parseError)
            return []
          }
        }
        
        if (data?.status === 'success') {
          const payload = data?.data
          return Array.isArray(payload) ? payload : (payload?.assigned_children || [])
        }
        return []
      })
      .catch(error => {
        this.show_error('Error fetching assigned children:', error)
        return []
      })
    },

    assign_on_sub_entity_change() {
      this.assign_form_list = []
      this.assign_selected_form_id = ''

      const entity_type_id = Number(this.assign_sub_entity_type_id || 0)
      if (!entity_type_id) return

      this.is_loading = true
      
      axios.get(constants.GET_ENTITY_TYPE_FORMS, {
        headers: this.headers,
        params: { entity_type_id }
      })
      .then(res => {
        let data = res.data
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data)
          } catch (parseError) {
            this.show_error('Error getting forms:', parseError)
            data = {}
          }
        }
        
        this.assign_form_list = Array.isArray(data?.data) ? data.data : []
      })
      .catch(error => {
        this.show_error('Error loading forms: ' + error.message)
      })
      .finally(() => {
        this.is_loading = false
      })
    },

    save_asset() {
      this.validation_error = null
      this.is_saving = true

      // Validation checks
      if (!this.assign_entity_name.trim()) {
        this.validation_error = "Please enter an asset name."
        this.is_saving = false
        return
      }

      const child_type_id = Number(this.assign_sub_entity_type_id || 0)
      if (!child_type_id) {
        this.validation_error = "Please select an asset type."
        this.is_saving = false
        return
      }

      const selected_form_id = Number(this.assign_selected_form_id || 0)
      if (!selected_form_id) {
        this.validation_error = "Please select a form for the asset."
        this.is_saving = false
        return
      }

      const parentEntityTypeId = this.parentEntityTypeId || 
                                 this.$route?.query?.entity_type_id || 
                                 sessionStorage.getItem('entity_type_id')

      const payload = {
        parent_application_id: Number(this.applicationId),
        entity_type_id: child_type_id,
        parent_entity_type_id: Number(parentEntityTypeId),
        parent_entity_profile_id: Number(this.entityProfileId),
        project_id: Number(this.projectId),
        form_ids: [selected_form_id],
        entity_name: this.assign_entity_name.trim(),
      }

      axios.post(constants.CREATE_ENTITY_APPLICATION_ONLY, payload, { headers: this.headers })
        .then(response => {
          let response_data = response.data
          if (typeof response_data === 'string') {
            try {
              response_data = JSON.parse(response_data)
            } catch (parseError) {
              this.show_error('Error getting response:', parseError)
              this.is_saving = false
              return
            }
          }

          if (response_data?.status !== "success") {
            const error_msg = response_data?.message || "Failed to create asset."
            this.validation_error = error_msg
            this.is_saving = false
            return
          }

          this.show_success("Asset created successfully!")
          
          this.close_modal('add_asset_modal')
          
          this.reset_modal()
          return this.load_assets()
        })
        .then(() => {
          this.$emit('asset-created')
        })
        .catch(error => {
          const msg = error?.response?.data?.message || error.message || "Error creating the asset."
          this.validation_error = msg
          this.show_error(msg)
        })
        .finally(() => {
          this.is_saving = false
        })
    },

    reset_modal() {
      this.assign_sub_entity_type_id = ''
      this.assign_form_list = []
      this.assign_selected_form_id = ''
      this.assign_entity_name = ''
      this.validation_error = null
      this.is_saving = false
      this.is_loading = false
    },

    view_asset(asset) {
      this.$emit('view-asset', asset)
    }
  }
}